import { components } from "@/api-schema";
import { RelocationOrderBranchRejectStatus } from "@/api-schema-enums";
import { Button, ButtonVariant, ButtonColorVariant } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertTriangle, CarFront, Key, PackageX } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RejectReasonOption } from "./reject-reason-option";
import { VehicleSummaryCard } from "./vehicle-summary-card";

type Vehicle = components["schemas"]["Vehicle"];

interface RejectVehicleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: RelocationOrderBranchRejectStatus) => void;
  vehicle?: Vehicle;
}

export function RejectVehicleDialog({ open, onOpenChange, onConfirm, vehicle }: RejectVehicleDialogProps) {
  const { t } = useTranslation(["fit", "common"]);
  const [selectedReason, setSelectedReason] = useState<RelocationOrderBranchRejectStatus | null>(null);

  const rejectReasons: Array<{
    value: RelocationOrderBranchRejectStatus;
    label: string;
    description: string;
    icon: React.ReactNode;
  }> = useMemo(
    () => [
      {
        value: RelocationOrderBranchRejectStatus.BranchRejectedDamagedVehicle,
        label: t("fit:VehicleDamagedReason"),
        description: t("fit:VehicleShowsDamage"),
        icon: <AlertTriangle className="h-5 w-5" />,
      },
      {
        value: RelocationOrderBranchRejectStatus.BranchRejectedMissingKeys,
        label: t("fit:KeysMissing"),
        description: t("fit:VehicleKeysNotAvailable"),
        icon: <Key className="h-5 w-5" />,
      },
      {
        value: RelocationOrderBranchRejectStatus.BranchRejectedMissingVehicle,
        label: t("fit:VehicleNotAvailable"),
        description: t("fit:VehicleNotDelivered"),
        icon: <PackageX className="h-5 w-5" />,
      },
      {
        value: RelocationOrderBranchRejectStatus.BranchRejectedWrongVehicle,
        label: t("fit:WrongVehicle"),
        description: t("fit:DifferentVehicleWasDelivered"),
        icon: <CarFront className="h-5 w-5" />,
      },
    ],
    [t],
  );

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSelectedReason(null);
    }
    onOpenChange(newOpen);
  };

  const handleConfirm = () => {
    if (selectedReason) {
      onConfirm(selectedReason);
      setSelectedReason(null);
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("fit:ReportProblem")}</DialogTitle>
          <DialogDescription>{t("fit:PleaseSelectProblemToReport")}</DialogDescription>
        </DialogHeader>

        {vehicle && <VehicleSummaryCard vehicle={vehicle} />}

        <div className="space-y-2 py-4">
          {rejectReasons.map((reason) => (
            <RejectReasonOption
              key={reason.value}
              value={reason.value}
              label={reason.label}
              description={reason.description}
              icon={reason.icon}
              isSelected={selectedReason === reason.value}
              select={setSelectedReason}
            />
          ))}
        </div>
        <DialogFooter>
          <Button variant={ButtonVariant.Outlined} colorVariant={ButtonColorVariant.Secondary} onClick={handleCancel}>
            {t("common:Cancel")}
          </Button>
          <Button
            variant={ButtonVariant.Filled}
            colorVariant={ButtonColorVariant.Error}
            onClick={handleConfirm}
            disabled={!selectedReason}
          >
            {t("fit:ReportProblem")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
